import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wrench, 
  ArrowRight, 
  Send, 
  CheckCircle2, 
  XCircle, 
  FileText, 
  Zap, 
  DollarSign, 
  Calendar,
  BookOpen,
  Key,
  Download
} from 'lucide-react';
import { getBackendUrl } from '../utils/backendUrl';

const ApiDocumentation: FC = () => {
  const navigate = useNavigate();

  const handleViewApiDocs = () => {
    const backendUrl = getBackendUrl();
    window.open(`${backendUrl}/api/docs`, '_blank');
  };

  const handleGetApiKey = () => {
    // Check if we're in admin or user context
    const isAdmin = window.location.pathname.startsWith('/admin');
    navigate(isAdmin ? '/admin/api-keys' : '/api-keys');
  };

  const coreEndpoints = [
    { method: 'GET', path: '/balance/{company_id}', description: 'Check balance' },
    { method: 'POST', path: '/settlements', description: 'Execute settlement' },
    { method: 'GET', path: '/settlements/{id}', description: 'Get status' },
    { method: 'GET', path: '/rates', description: 'Exchange rates' },
    { method: 'GET', path: '/transactions', description: 'History' },
  ];

  const webhooks = [
    { event: 'settlement.initiated', icon: Send, color: 'text-blue-400' },
    { event: 'settlement.completed', icon: CheckCircle2, color: 'text-green-400' },
    { event: 'settlement.failed', icon: XCircle, color: 'text-red-400' },
    { event: 'daily.summary', icon: FileText, color: 'text-gray-400' },
  ];

  const rateLimits = [
    { label: '1000 requests / minute', icon: Zap, color: 'text-yellow-400' },
    { label: '$10M per transaction', icon: DollarSign, color: 'text-green-400' },
    { label: '$100M daily limit', icon: Calendar, color: 'text-blue-400' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Quick Start</h1>
      </div>

      {/* Quick Start Section */}
      <div className="bg-[#182233] rounded-lg border border-gray-600 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-2 bg-[#2777EF]/20 rounded-lg">
            <Wrench className="w-6 h-6 text-[#2777EF]" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white mb-2">Get your API key from dashboard</h2>
            <p className="text-gray-400 mb-4">Make your first call:</p>
            
            {/* API Request Example */}
            <div className="mb-4">
              <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase">Request</span>
                  <span className="text-xs text-gray-500">POST /settlements</span>
                </div>
                <pre className="text-sm text-gray-300 font-mono overflow-x-auto">
{`{
  "from": "walmart_balance_id",
  "to": "target_balance_id",
  "amount": 100000,
  "client_id": "unique_tx_123"
}`}
                </pre>
              </div>
            </div>

            {/* API Response Example */}
            <div>
              <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase">Response</span>
                  <span className="text-xs text-gray-500">(2.8 seconds)</span>
                </div>
                <pre className="text-sm text-gray-300 font-mono overflow-x-auto">
{`{
  "id": "stl_abc123",
  "status": "completed",
  "duration": "2.8s",
  "saved": "$2.420"
}`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Core Endpoints and Webhooks/Rate Limits Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Core Endpoints */}
        <div className="lg:col-span-1 bg-[#182233] rounded-lg border border-gray-600 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Core Endpoints</h2>
          <div className="space-y-3">
            {coreEndpoints.map((endpoint, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-[#0E131A] rounded-lg border border-gray-700 hover:border-[#2777EF]/50 transition-colors cursor-pointer group"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                      endpoint.method === 'GET' 
                        ? 'bg-green-900/30 text-green-400' 
                        : 'bg-blue-900/30 text-blue-400'
                    }`}>
                      {endpoint.method}
                    </span>
                    <span className="text-sm text-gray-300 font-mono">{endpoint.path}</span>
                  </div>
                  <p className="text-xs text-gray-400">{endpoint.description}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-[#2777EF] transition-colors" />
              </div>
            ))}
          </div>
        </div>

        {/* Webhooks and Rate Limits */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Webhooks */}
          <div className="bg-[#182233] rounded-lg border border-gray-600 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Webhooks</h2>
            <div className="space-y-3">
              {webhooks.map((webhook, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-[#0E131A] rounded-lg border border-gray-700"
                >
                  <webhook.icon className={`w-5 h-5 ${webhook.color}`} />
                  <span className="text-sm text-gray-300 font-mono">{webhook.event}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Limits */}
          <div className="bg-[#182233] rounded-lg border border-gray-600 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Rate Limits</h2>
            <div className="space-y-3">
              {rateLimits.map((limit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-[#0E131A] rounded-lg border border-gray-700"
                >
                  <limit.icon className={`w-5 h-5 ${limit.color}`} />
                  <span className="text-sm text-gray-300">{limit.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleViewApiDocs}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-[#2777EF] text-white rounded-lg hover:bg-[#0053E2] transition-colors font-medium"
        >
          <BookOpen className="w-5 h-5" />
          View API Docs
        </button>
        <button
          onClick={handleGetApiKey}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-[#2777EF] text-white rounded-lg hover:bg-[#0053E2] transition-colors font-medium"
        >
          <Key className="w-5 h-5" />
          Get API Key
        </button>
        <button
          onClick={() => window.open('https://loopchain.io/contact', '_blank')}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-[#2777EF] text-white rounded-lg hover:bg-[#0053E2] transition-colors font-medium"
        >
          <Download className="w-5 h-5" />
          Request Specific API Integration
        </button>
      </div>

      {/* Footer */}
      <div className="text-center text-gray-400 text-sm py-4">
        © 2025 LoopChain
      </div>
    </div>
  );
};

export default ApiDocumentation;


